import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RoundName } from "../../domain/round/types";
import { api, type TournamentSummary } from "../../lib/api";
import {
  initialRoundName,
  initialSeason,
  initialTournamentId,
  persistRoundSelection,
} from "../../lib/roundSelection";
import { carBlockApi, type CarBlockRow } from "./carBlockApi";
import "../rounds/round-workspace.css";
import "./transport-page.css";
import "./car-blocks.css";

const ROUNDS: RoundName[] = ["R1", "R2", "R3", "R4"];

function chooseTournament(rows: TournamentSummary[]) {
  if (!rows.length) return "";
  const today = new Date().toISOString().slice(0, 10);
  const started = rows.filter((row) => row.startDate && row.startDate <= today);
  return (started.at(-1) || rows[0]).id;
}

function clock(minutes: number) {
  const value = Math.max(0, Math.round(Number(minutes) || 0));
  return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
}

function peopleLabel(block: CarBlockRow) {
  return block.people.map((person) => person.name || person.id).filter(Boolean);
}

export function CarBlockReorderPanel() {
  const queryClient = useQueryClient();
  const [season, setSeason] = useState(initialSeason);
  const [tournamentId, setTournamentId] = useState(initialTournamentId);
  const [round, setRound] = useState<RoundName>(initialRoundName);
  const [targets, setTargets] = useState<Record<number, string>>({});
  const [notice, setNotice] = useState("");

  const tournaments = useQuery({
    queryKey: ["tournaments", season],
    queryFn: () => api.tournaments(season),
  });
  useEffect(() => {
    const rows = tournaments.data?.tournaments || [];
    if (rows.length && (!tournamentId || !rows.some((row) => row.id === tournamentId))) {
      setTournamentId(chooseTournament(rows));
    }
  }, [tournaments.data, tournamentId]);
  useEffect(() => {
    if (tournamentId) persistRoundSelection(tournamentId, round, season);
    setTargets({});
    setNotice("");
  }, [tournamentId, round, season]);

  const workspace = useQuery({
    queryKey: ["round-workspace", tournamentId, round],
    queryFn: () => api.roundWorkspace(tournamentId, round),
    enabled: Boolean(tournamentId),
  });
  const blocks = useQuery({
    queryKey: ["car-blocks", tournamentId, round],
    queryFn: () => carBlockApi.list(tournamentId, round),
    enabled: Boolean(tournamentId && workspace.data?.transport),
  });

  const reorder = useMutation({
    mutationFn: (action: { blockNumber: number; targetBlockNumber?: number; direction?: "up" | "down" }) => {
      if (!workspace.data) throw new Error("Brak aktualnego workspace rundy.");
      return carBlockApi.reorder(tournamentId, round, {
        expectedRevision: workspace.data.workspaceRevision,
        ...action,
      });
    },
    onSuccess: async (result, action) => {
      const changed = result.changedPersonIds?.length || 0;
      const actionText = action.targetBlockNumber
        ? `AUTO ${action.blockNumber} ↔ AUTO ${action.targetBlockNumber}`
        : `AUTO ${action.blockNumber} ${action.direction === "up" ? "wyżej" : "niżej"}`;
      setNotice(
        `Zapisano ${actionText}. Zmieniono ${changed} osób; plan i transport są przeliczone. Runda wymaga ponownego zatwierdzenia.`,
      );
      setTargets({});
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["round-workspace", tournamentId, round] }),
        queryClient.invalidateQueries({ queryKey: ["car-blocks", tournamentId, round] }),
      ]);
    },
  });

  const selectedTournament = tournaments.data?.tournaments.find((item) => item.id === tournamentId);
  const rows = blocks.data?.blocks || [];
  const error = reorder.error || blocks.error || workspace.error || tournaments.error;
  const errorMessage = error instanceof Error ? error.message : "";

  function swap(blockNumber: number) {
    const target = Number(targets[blockNumber] || 0);
    if (!target) {
      setNotice(`Wybierz auto, z którym ma zostać zamienione AUTO ${blockNumber}.`);
      return;
    }
    reorder.mutate({ blockNumber, targetBlockNumber: target });
  }

  return (
    <section className="page-grid car-block-page">
      <article className="panel round-context-panel">
        <div className="round-context-copy">
          <span className="eyebrow">CAŁE BLOKI SAMOCHODOWE</span>
          <h2>{selectedTournament?.name || "Wybierz turniej"}</h2>
          <p>
            AUTO 1, AUTO 2 itd. oznaczają kolejne poranne bloki w aktualnym Dojeździe. Zamiana przenosi cały skład auta jako jeden blok i automatycznie przelicza transport.
          </p>
        </div>
        <div className="round-context-controls">
          <label><span>Sezon</span><select value={season} onChange={(event) => setSeason(Number(event.target.value))}>{[2026, 2025, 2024].map((value) => <option key={value}>{value}</option>)}</select></label>
          <label className="tournament-select"><span>Turniej</span><select value={tournamentId} onChange={(event) => setTournamentId(event.target.value)}>{(tournaments.data?.tournaments || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <div className="round-switch">{ROUNDS.map((value) => <button key={value} type="button" className={round === value ? "active" : ""} onClick={() => setRound(value)}>{value}</button>)}</div>
        </div>
      </article>

      <article className="panel car-block-command-hint">
        <div><span className="eyebrow">TA SAMA LOGIKA NA DISCORDZIE</span><strong>zamień auto 1 z auto 3 {round.toLowerCase()}</strong></div>
        <p>Jeśli runda była już zatwierdzona, zmiana otworzy ją do ponownego zatwierdzenia.</p>
      </article>

      {notice || errorMessage ? <div className={`transport-editor-notice ${errorMessage ? "error" : ""}`}>{errorMessage || notice}</div> : null}

      {workspace.data?.confirmation.confirmed ? (
        <div className="transport-editor-notice car-block-confirmed-note">
          Ta runda jest obecnie zatwierdzona. Pierwsza zmiana bloku automatycznie otworzy ją do edycji i po zmianie trzeba ją zatwierdzić ponownie.
        </div>
      ) : null}

      {blocks.isPending || workspace.isPending ? (
        <article className="panel round-loading"><div className="round-loading-bar" /><span>Wczytuję bloki aut…</span></article>
      ) : !workspace.data?.transport ? (
        <article className="panel round-empty">Ta runda nie ma jeszcze transportu. Najpierw wygeneruj lub przelicz Dojazd.</article>
      ) : rows.length < 2 ? (
        <article className="panel round-empty">Nie ma co najmniej dwóch porannych bloków aut do zamiany.</article>
      ) : (
        <article className="panel car-block-panel">
          <header className="round-section-head">
            <div><span className="eyebrow">DOJAZD · BLOKI</span><h3>Przestaw całe auta</h3></div>
            <span className="transport-board-count">{rows.length} aut</span>
          </header>
          <div className="car-block-list">
            {rows.map((block, index) => {
              const people = peopleLabel(block);
              const targetOptions = rows.filter((candidate) => candidate.number !== block.number && candidate.contiguous);
              return (
                <article key={block.id} className={`car-block-row ${block.contiguous ? "" : "invalid"}`}>
                  <div className="car-block-number"><small>AUTO</small><strong>{block.number}</strong></div>
                  <div className="car-block-main">
                    <div className="car-block-headline"><strong>{clock(block.departAtMinutes)}</strong><span>{people.length} os.</span>{!block.contiguous ? <b>NIECIĄGŁY BLOK</b> : null}</div>
                    <div className="car-block-people">{people.map((name) => <span key={name}>{name}</span>)}</div>
                  </div>
                  <div className="car-block-actions">
                    <button type="button" className="secondary-button" disabled={reorder.isPending || !block.contiguous || index === 0} onClick={() => reorder.mutate({ blockNumber: block.number, direction: "up" })}>↑ Wyżej</button>
                    <button type="button" className="secondary-button" disabled={reorder.isPending || !block.contiguous || index === rows.length - 1} onClick={() => reorder.mutate({ blockNumber: block.number, direction: "down" })}>↓ Niżej</button>
                    <select disabled={reorder.isPending || !block.contiguous} value={targets[block.number] || ""} onChange={(event) => setTargets((current) => ({ ...current, [block.number]: event.target.value }))}>
                      <option value="">Zamień z…</option>
                      {targetOptions.map((candidate) => <option key={candidate.id} value={candidate.number}>AUTO {candidate.number} · {clock(candidate.departAtMinutes)}</option>)}
                    </select>
                    <button type="button" className="primary-button" disabled={reorder.isPending || !block.contiguous || !targets[block.number]} onClick={() => swap(block.number)}>{reorder.isPending ? "Zapisuję…" : "Zamień"}</button>
                  </div>
                </article>
              );
            })}
          </div>
        </article>
      )}
    </section>
  );
}
